
import { useState } from 'react';
import { TradeType } from '../page';

interface ApplicationReviewProps {
  formData: any;
  onNext: () => void;
  onBack: () => void;
}

export default function ApplicationReview({ formData, onNext, onBack }: ApplicationReviewProps) {
  // Document upload states
  const [selfie, setSelfie] = useState<File | null>(null);
  const [photoId, setPhotoId] = useState<File | null>(null);
  const [cscsCard, setCscsCard] = useState<File | null>(null);
  const [dbsCheck, setDbsCheck] = useState<File | null>(null);
  const [insurance, setInsurance] = useState<File | null>(null);
  const [qualifications, setQualifications] = useState<File | null>(null);
  const [drivingLicense, setDrivingLicense] = useState<File | null>(null);
  const [vehicleInsurance, setVehicleInsurance] = useState<File | null>(null);
  const [motCertificate, setMotCertificate] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (setter: (file: File | null) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setter(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check required documents based on trade
    if (!selfie) {
      alert('Selfie photo is required');
      return;
    }

    if (!photoId) {
      alert('Photo ID is required');
      return;
    }

    if (!cscsCard) {
      alert('CSCS Card is required');
      return;
    }

    // Transportation specific requirements
    if (formData.trade === 'transportation') {
      if (!drivingLicense) {
        alert('Valid UK Driving License is required for Transportation providers');
        return;
      }
      if (!vehicleInsurance) {
        alert('Vehicle Insurance is required for Transportation providers');
        return;
      }
      if (!motCertificate) {
        alert('MOT Certificate is required for Transportation providers');
        return;
      }
    }

    setIsUploading(true);

    try {
      // TODO: Upload documents to Supabase Storage
      // For now, just proceed to next step
      
      alert('Documents uploaded successfully! You can now start accepting jobs.');
      onNext();
    } catch (error: any) {
      alert('Failed to upload documents. Please try again.');
      console.error('Document upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipForNow = () => {
    if (window.confirm('You can upload documents later from your dashboard. However, you must upload all required documents before accepting jobs. Continue?')) {
      onNext();
    }
  };

  const tradeLabels: Record<TradeType, string> = {
    handyman: 'Handyman',
    electrician: 'Electrician',
    plumber: 'Plumber',
    waste: 'Waste Removal',
    transportation: 'Transportation',
    cleaning: 'Cleaning'
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#0B3C5D] to-[#0B3C5D] rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-file-upload-line text-white text-3xl"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Upload Required Documents</h2>
        <p className="text-gray-600 text-center">Upload your documents to complete your profile</p>
      </div>

      {/* Important Notice */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-2xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-2">Documents Required Before Accepting Jobs</h3>
            <p className="text-white/90 mb-3">
              You must upload all required documents before you can start accepting jobs. Some documents can be uploaded later from your dashboard.
            </p>
            <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
              <p className="text-sm text-white/90">
                <strong>Your Trade:</strong> {tradeLabels[formData.trade as TradeType]}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document Upload Section */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-folder-upload-line text-[#0B3C5D]"></i>
            Required Documents for All Trades
          </h3>

          <div className="space-y-4">
            {/* Selfie - Required */}
            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Selfie Photo <span className="text-red-500">*</span>
                <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload a clear selfie photo of yourself. This helps us verify your identity.
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange(setSelfie)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
              />
              {selfie && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {selfie.name}
                </p>
              )}
            </div>

            {/* Photo ID - Required */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Photo ID (Passport or Driving License) <span className="text-red-500">*</span>
                <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload a clear photo of your passport or driving license
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange(setPhotoId)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0B3C5D]/10 file:text-[#0B3C5D] hover:file:bg-[#0B3C5D]/20 cursor-pointer"
              />
              {photoId && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {photoId.name}
                </p>
              )}
            </div>

            {/* CSCS Card - Required */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                CSCS Card <span className="text-red-500">*</span>
                <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload a photo of your valid CSCS card
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange(setCscsCard)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0B3C5D]/10 file:text-[#0B3C5D] hover:file:bg-[#0B3C5D]/20 cursor-pointer"
              />
              {cscsCard && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {cscsCard.name}
                </p>
              )}
            </div>

            {/* DBS Check - Optional */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                DBS Check
                <span className="text-xs font-normal text-blue-600 ml-2">(Optional - Can upload later from dashboard)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload your DBS check certificate if you have one
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange(setDbsCheck)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              {dbsCheck && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {dbsCheck.name}
                </p>
              )}
            </div>

            {/* Insurance - Optional */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Public Liability Insurance
                <span className="text-xs font-normal text-blue-600 ml-2">(Optional - Can upload later from dashboard)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload your public liability insurance certificate
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange(setInsurance)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              {insurance && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {insurance.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Trade-Specific Documents */}
        {(formData.trade === 'electrician' || formData.trade === 'plumber') && (
          <div className="bg-white rounded-xl border-2 border-amber-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-award-line text-amber-600"></i>
              Trade Qualifications
            </h3>

            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {formData.trade === 'electrician' ? 'Electrical' : 'Plumbing'} Qualifications
                <span className="text-xs font-normal text-blue-600 ml-2">(Optional - Can upload later from dashboard)</span>
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Upload your trade qualification certificates
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange(setQualifications)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              {qualifications && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <i className="ri-check-line"></i>
                  {qualifications.name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Transportation Specific Documents */}
        {formData.trade === 'transportation' && (
          <div className="bg-white rounded-xl border-2 border-indigo-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-truck-line text-indigo-600"></i>
              Transportation Documents (Required)
            </h3>

            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Valid UK Driving License <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Upload both sides of your valid UK driving license
                </p>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange(setDrivingLicense)}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 cursor-pointer"
                />
                {drivingLicense && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    {drivingLicense.name}
                  </p>
                )}
              </div>

              <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Vehicle Insurance <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Upload your current vehicle insurance certificate
                </p>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange(setVehicleInsurance)}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 cursor-pointer"
                />
                {vehicleInsurance && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    {vehicleInsurance.name}
                  </p>
                )}
              </div>

              <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  MOT Certificate <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-600 ml-2">(Required now)</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Upload your vehicle's valid MOT certificate
                </p>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange(setMotCertificate)}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 cursor-pointer"
                />
                {motCertificate && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    {motCertificate.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
          <div className="flex items-start gap-3">
            <i className="ri-information-fill text-green-600 text-2xl mt-0.5"></i>
            <div>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Important:</strong> Documents marked with <span className="text-red-500">*</span> are required before you can accept jobs.
              </p>
              <p className="text-sm text-gray-700">
                Optional documents can be uploaded anytime from your provider dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isUploading}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSkipForNow}
            disabled={isUploading}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload Later
          </button>
          <button
            type="submit"
            disabled={isUploading}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:from-[#0B3C5D]/90 hover:to-[#0B3C5D]/90 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <span>Upload & Complete</span>
                <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
